import {assignmentTypes, aosTaskTypes} from "Constants/constants";
import {COPY_STRINGS} from "Constants/translatedCopy";
import {FEATURE_FLAGS} from "Constants/featureFlags";
import { isBTSEffective } from "./entitlementService";
import { getCopyByGradeband } from "./ThemingService";

const introTextByActivityType = {
  [assignmentTypes.DYSLEXIA]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  }, //this has to be a function because otherwise it will be evaluated before the language is set and will not work as expected for non english markets
  [assignmentTypes.NWF]: () => {return COPY_STRINGS.nwf_instructions;},
  [assignmentTypes.SPELLING]: () => {return COPY_STRINGS.spelling_intro;},
  [assignmentTypes.ASSESSMENT]: (session) => 
		session.assessmentPageNavEnabled ? 
			getCopyByGradeband(COPY_STRINGS, 'orf_with_page_navigation_instructions') : 
			getCopyByGradeband(COPY_STRINGS, 'orf_instructions'),
  [assignmentTypes.LISTENING_COMPREHENSION]: () => {return COPY_STRINGS.listening_comprehension_intro;},
  [assignmentTypes.READING_COMPREHENSION]: () => {return getCopyByGradeband(COPY_STRINGS, 'reading_comprehension_intro');},
  [assignmentTypes.VOCABULARY_SCREENER]: () => {return COPY_STRINGS.vocabulary_screener_intro;},
  [assignmentTypes.LETTER_NAMING_AND_SOUNDS]: () => {return COPY_STRINGS.letter_name_and_sounds_intro;},
};

const introTextByActivityTypeAOS = {
  [aosTaskTypes.ran]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.wordPartDeletion]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.upAndDown]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.changeOnePart]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.wif]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.nwf]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.nonsenseWordRepetition]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.letterName]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.letterSound]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.beginningLetterSound]: (session, isNonVerbalAssignment) => {
    return isNonVerbalAssignment ? COPY_STRINGS.dyslexia_instructions_non_verbal : COPY_STRINGS.dyslexia_instructions;
  },
  [aosTaskTypes.assessment]: (session) => session.assessmentPageNavEnabled ? COPY_STRINGS.orf_with_page_navigation_instructions : COPY_STRINGS.orf_instructions,
};

export default function getActivityIntroText(
  activityType,
  session,
  useAOS,
  isNonVerbalAssignment,
  isSpanishPilot = false,
) {
  /*
   * Spanish listening-comprehension intro (ASSMNT-1003, post-pilot resolution of ASSMNT-706):
   * the es-mx LC intro is suppressed until it is enabled for the session — either the district
   * holds the evaluarBTS license (isBTSEffective) OR the fleet-wide ENABLE_SPANISH_LC_INTRO flag
   * is on (URL override: ?forceEnableSpanishLcIntro=true). The license is OR'd (not AND'd) with
   * the flag — mirroring the Spanish-AOS gate in util.js — so licensed districts can validate the
   * intro in production without flipping the flag fleet-wide. This keys off the Spanish *content*
   * (es-mx locale or the isSpanishPilot URL param) rather than the pilot flag alone, so it stays
   * correct for real es-mx assignments once Spanish is generally available. When enabled, the
   * existing es-mx copy (COPY_STRINGS.listening_comprehension_intro) plays.
   */
  const isSpanishContent = isSpanishPilot || session?.locale === "es-mx";
  const isSpanishLcIntroEnabled = isBTSEffective() || FEATURE_FLAGS.ENABLE_SPANISH_LC_INTRO;
  const isListeningComprehension =
    activityType === assignmentTypes.LISTENING_COMPREHENSION ||
    activityType === aosTaskTypes.listeningComprehension;
  if (
    isSpanishContent &&
    !isSpanishLcIntroEnabled &&
    isListeningComprehension
  ) {
    return () => undefined;
  }

  let intro = introTextByActivityType[activityType] || introTextByActivityType[assignmentTypes.ASSESSMENT];

  const shouldPreserveSpanishLcIntro =
    isSpanishContent && isSpanishLcIntroEnabled && isListeningComprehension;

  if (useAOS && !shouldPreserveSpanishLcIntro) {
    intro = introTextByActivityTypeAOS[activityType] || (() => undefined);
  }

  return intro.bind(this, session, isNonVerbalAssignment);
}
